import React, { useState, useEffect } from 'react';
import { cn } from '@anytag/shared-utils';
import { Button } from '@anytag/shared-atoms';
import { VideoItem, Rule } from '../../types';
import { exportToCSV, exportToSpreadsheet } from '../../services/export-utils';
import { AnalysisSummaryTable } from './analysis-summary-table';
import { AnalysisIndividualView } from './analysis-individual-view';

interface AnalysisReportProps {
  items: VideoItem[];
  rules: Rule[];
  isLoading: boolean;
}

type ViewMode = 'INDIVIDUAL' | 'SUMMARY';

export const AnalysisReport: React.FC<AnalysisReportProps> = ({ items, rules, isLoading }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('INDIVIDUAL');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const doneItems = items.filter((i) => i.status === 'DONE' && i.result);
  const selectedItem = items.find((i) => i.id === selectedId);

  useEffect(() => {
    if (items.length > 0 && !selectedId) {
      setSelectedId(items[0].id);
    }
    if (selectedId && !selectedItem && items.length > 0) {
      setSelectedId(items[0].id);
    }
  }, [items, selectedId, selectedItem]);

  const activeItem = selectedItem || items[0];
  const getInfluencerName = (fileName: string) => (fileName ? fileName.replace(/\.mp4$/i, '') : 'Unknown Video');

  if (items.length === 0) {
    return (
      <div className="flex h-full min-h-100 flex-col items-center justify-center rounded-xl border-base-border bg-white p-8 text-center opacity-50">
        <svg className="mb-4 h-20 w-20 text-black-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1}
            d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <p className="text-lg text-tertiary">Upload videos and click "Analyze now" to begin.</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border-base-border bg-white shadow-sm">
      <div className="flex flex-shrink-0 items-center justify-between gap-4 border-b-border p-2">
        <div className="flex rounded-lg border-base-border p-1">
          <button
            onClick={() => setViewMode('INDIVIDUAL')}
            className={cn(styles.button.base, {
              [styles.button.active]: viewMode === 'INDIVIDUAL',
              [styles.button.inactive]: viewMode !== 'INDIVIDUAL'
            })}
          >
            Individual
          </button>
          <button
            onClick={() => setViewMode('SUMMARY')}
            className={cn(styles.button.base, {
              [styles.button.active]: viewMode === 'SUMMARY',
              [styles.button.inactive]: viewMode !== 'SUMMARY'
            })}
          >
            Summary
          </button>
        </div>

        {viewMode === 'INDIVIDUAL' && (
          <div className="mx-2 flex custom-scrollbar flex-1 overflow-x-auto">
            {items.map((item) => (
              <button
                key={item.id}
                onClick={() => setSelectedId(item.id)}
                className={`mx-1 flex items-center gap-2 rounded-t-lg px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors ${
                  selectedId === item.id
                    ? 'border-x border-t border-black-30 bg-white text-red-60'
                    : 'cursor-pointer text-secondary hover:bg-black-10 hover:text-primary'
                }`}
              >
                {item.status === 'DONE' ? (
                  <span className={cn(styles.status, 'bg-attention-green')}></span>
                ) : item.status === 'PROCESSING' ? (
                  <span className={cn(styles.status, 'animate-pulse bg-attention-yellow')}></span>
                ) : item.status === 'ERROR' ? (
                  <span className={cn(styles.status, 'bg-attention-red')}></span>
                ) : (
                  <span className={cn(styles.status, 'bg-attention-blue')}></span>
                )}
                {getInfluencerName(item.fileName)}
              </button>
            ))}
          </div>
        )}

        {doneItems.length > 0 && (
          <div className="flex gap-2">
            <Button
              title="CSV"
              variant="outline"
              onClick={() => exportToCSV(items, rules)}
              prefixIcon={{ icon: 'download', size: '14px' }}
              tooltip={{ help: 'Download as CSV' }}
            />
            <Button
              title="Spreadsheet"
              variant="solid"
              onClick={() => exportToSpreadsheet(items, rules)}
              prefixIcon={{ icon: 'file-text', size: '14px' }}
              tooltip={{ help: 'Download as Spreadsheet (XLS)' }}
            />
          </div>
        )}
      </div>

      <div className="relative flex custom-scrollbar min-h-50 flex-1 flex-col space-y-4 overflow-y-auto bg-white p-2">
        {viewMode === 'INDIVIDUAL' && <AnalysisIndividualView activeItem={activeItem} rules={rules} />}
        {viewMode === 'SUMMARY' && <AnalysisSummaryTable items={items} rules={rules} isLoading={isLoading} />}
      </div>
    </div>
  );
};

const styles = {
  button: {
    base: cn('rounded-md px-3 py-1.5 text-xs font-bold transition-colors'),
    active: cn('border-base-border bg-white text-red-60 shadow'),
    inactive: cn('cursor-pointer text-black-60 hover:text-black-90')
  },
  status: cn('size-1.5 rounded-full')
};
